import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import ButtonWithPreloader from 'UI/Button/ButtonWithPreloader'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import SwitchExtended from 'UI/Switch/SwitchExtended'
import SelectComponent from 'UI/Select'
import { ReactComponent as ArrowLeftIcon } from 'assets/svg/ArrowLeftIcon.svg'
import { ReactComponent as SplitIcon } from 'assets/svg/SplitIcon.svg'
import { ReactComponent as CalendarIcon } from 'assets/svg/CalendarIcon.svg'
import { ReactComponent as PlusIcon } from 'assets/svg/PlustIcon.svg'
import { ReactComponent as InfoIcon } from 'assets/svg/InfoIcon.svg'
import Checkbox from 'UI/Checkbox'
import { queryCache, useMutation, useQuery } from 'react-query'
import { DEAL_INFO_URL, NEW_DEAL_URL } from 'constants/routes'
import { useSnackbar } from 'notistack'
import { calculateReverseSchedule, createInhouseDeal, editInhouseDeal, getDeal, getFlexScheduleByDeal } from 'services/deals'
import {
  DealNotificationType,
  EarlyDiscountOptionType,
  extendTypes,
  ICreateUpdateInhouseDeal,
  IEarlyDiscount,
  IEarlyDiscountOptionDto,
  IExternalDeal,
  IFeeDistributionOption,
  IFlexDisbursement,
  IMerchantPosition,
  INotificationSubscriptionPreferences,
  INotificationSubscriptionSetting,
  ICustomPaymentSchedule,
} from 'typescript/interfaces/deals'
import { useFormik } from 'formik'
import ClientAutoComplete from 'components/ClientAutoComplete'
import ISORepSelector from 'components/ISORepSelector'
import { paymentFrequency } from 'constants/paymentFrequency'
import { getPaymentDayFromDate, paymentDayShortLabels } from 'constants/paymentDay'
import { CompanyType } from 'typescript/interfaces/companies'
import { DECIMAL_PLACES, DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { checkForNaN } from 'utils/formatters'
import {
  COMMISSION_DATE_EARLIER,
  COMMISSION_DUE_DATE_IN_PAST,
  DEAL_START_DATE_IN_PAST,
  DISBURSEMENT_DATE_IN_PAST,
  FIRST_PAYMENT_DATE_IN_PAST,
  LENDER_DONT_HAVE_ENOUGHT_MONEY,
  SOMETHING_WENT_WRONG,
  SPLIT_COMMISSION_ERROR,
  PAYMENT_FREQUENCY_CHANGED_AFTER_APPROVAL
} from 'constants/errors'
import { COMMISION_DUE_DATE_WARNING, FIRST_PAYMENT_DATE_WARNING } from 'constants/warnings'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import Popover from '@material-ui/core/Popover'
import RequiredOption from 'UI/Select/RequiredOption'
import CompanyAutoComplete from 'components/CompanyAutoComplete'
import { getClientBankAccounts } from 'services/clients'
import { UserContext } from 'contexts/userContext'
import { AxiosError } from 'axios'
import { IRepListItemLight } from 'typescript/interfaces/rep'
import VendorAutoComplete from 'components/VendorAutoComplete'
import { DealsStatus } from 'typescript/enums/deals'
import { IVendorListLightItem } from 'typescript/interfaces/vendor'
import { usePermission } from 'hooks/usePermission'
import { ADD_VENDOR_TO_DEAL } from 'constants/permissions'
import { getRepLight } from 'services/rep'
import { getIsoLight } from 'services/iso'
import { IClientListItemLight } from 'typescript/interfaces/clients'
import { getStates } from 'services/states'
import { IIsoListItemLight } from 'typescript/interfaces/iso'
import Decimal from 'decimal.js'
import { IPaymentFrequency, PaymentFrequencyEnum } from 'typescript/interfaces/paymentFrequency'
import { PaymentDayEnum } from 'typescript/interfaces/paymentDay'
import useDebouncedEffect from 'hooks/Custom/useDebouncedEffect'
import ModalComponent from 'UI/Modal'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import usePopover from 'hooks/usePopover'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { bankRounding, bankRoundingAwayFromZero, bankRoundingDecimal, calcProportionInPercents } from 'utils/rounding'
import { getInitialValuesForCommission } from 'utils/commissionUtils'
import { calculateCustomScheduleDate } from 'utils/scheduleUtils'
import isValidDate from 'utils/isValidDate'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import convertDateForPicker from 'utils/convertDateForPicker'
import { getCurrentDate, isBeforeDate } from 'utils/dateUtils'
import DatePickerWithTooltip from 'components/DatePickerWithTooltip'
import SplitCommissionForm from './SplitCommissionForm'
import { schemaInHouseDeal } from './schema'
import EarlyDiscountForm from './EarlyDiscountForm'
import FlexDisbursementForm, { IFlexDisbursementForm, IFlexDisbursementFormValues } from './FlexDisbursementForm'
import MerchantPositionsForm from './MerchantPositionsForm'
import MerchantPositionsTable from './MerchantPositionsTable'
import ExternalDealsForm from './ExternalDealsForm'
import ExternalDealsTable from './ExternalDealsTable'
import { useDealInformationFormStyles } from './styles'
import BankFeeDistributionSelector from './BankFeeDistributionSelector'
import PaymentDaySelector from './PaymentDaySelector'
import NotificationSubscriptionForm from './NotificationSubscriptionForm'
import CustomizedScheduleModal from './CustomizedScheduleModal'

interface Props {
  changeStepsAvailable: (steps: number[]) => void
}

interface InhouseDealForm extends Omit<ICreateUpdateInhouseDeal, 'isoSplitCommission'> {
  clientState?: string
  isoSplitCommission: Array<{ id: number; value: Decimal }>
  initialIsoCommissionDueDate?: string
  initialRepCommissionDueDate?: string
  repCommissionTransactionsIsCreated?: boolean
  isoCommissionTransactionsIsCreated?: boolean
  isSubmitted?: boolean
  isRefiDeal?: boolean
  customPaymentSchedule?: ICustomPaymentSchedule
  initialStartDate?: string
  initialFirstPaymentDate?: string
  initialFlexSchedule?: IFlexDisbursement[]
}

const defaultNotificationSettings: INotificationSubscriptionSetting[] = [
  {
    admin: true,
    representative: true,
    iso: false,
    syndicator: false,
    merchant: false,
    notificationType: DealNotificationType.dealCollectedPercentage,
  }
]


const defaultRolelessNotificationSettings: INotificationSubscriptionSetting[] = [
  {
    admin: null,
    representative: null,
    iso: null,
    syndicator: null,
    merchant: null,
    notificationType: DealNotificationType.dealWasFunded,
  },
]

function getDateErrorMessage(description?: string): string | undefined {
  switch (description) {
    case 'DEAL_START_DATE_IN_PAST':
      return DEAL_START_DATE_IN_PAST
    case 'DISBURSEMENT_DATE_IN_PAST':
      return DISBURSEMENT_DATE_IN_PAST
    case 'FIRST_PAYMENT_DATE_IN_PAST':
      return FIRST_PAYMENT_DATE_IN_PAST
    case 'INVALID_FIRST_PAYMENT_DATE':
      return 'The first payment date cannot be earlier than the deal start date.'
    case 'COMMISSION_DUE_DATE_IN_PAST':
      return COMMISSION_DUE_DATE_IN_PAST
    case 'COMMISSION_DATE_EARLIER':
      return COMMISSION_DATE_EARLIER
    case 'ISO_COMMISSIONS_CREATED':
      return 'The iso commission due date cannot be edited because it has already been debited.'
    case 'REP_COMMISSIONS_CREATED':
      return 'The rep commission due date cannot be edited because it has already been debited.'
    case 'PAYMENT_FREQUENCY_CHANGED_AFTER_APPROVAL':
      return PAYMENT_FREQUENCY_CHANGED_AFTER_APPROVAL
    default:
      return undefined
  }
}

function mapEarlyDiscountOptionsToIEarlyDiscount(options: IEarlyDiscountOptionDto[]): IEarlyDiscount {
  const result: IEarlyDiscount = {}

  for (const option of options) {
    switch (option.type) {
      case EarlyDiscountOptionType.ThirtyDays:
        result.thirtyDays = option.factor
        break
      case EarlyDiscountOptionType.SixtyDays:
        result.sixtyDays = option.factor
        break
      case EarlyDiscountOptionType.NinetyDays:
        result.ninetyDays = option.factor
        break
      case EarlyDiscountOptionType.Custom:
        result.custom = option.factor
        result.customDays = option.days
        break
      default:
        break
    }

    if (!result.dayType) {
      result.dayType = option.dayType
    }
  }

  return result
}

function mapIEarlyDiscountToOptions(discount?: IEarlyDiscount & { customDays?: number }): IEarlyDiscountOptionDto[] {
  const result: IEarlyDiscountOptionDto[] = []

  if (!discount) {
    return result
  }

  if (discount.thirtyDays !== undefined) {
    result.push({
      type: EarlyDiscountOptionType.ThirtyDays,
      dayType: discount.dayType!,
      days: 30,
      factor: discount.thirtyDays,
    })
  }

  if (discount.sixtyDays !== undefined) {
    result.push({
      type: EarlyDiscountOptionType.SixtyDays,
      dayType: discount.dayType!,
      days: 60,
      factor: discount.sixtyDays,
    })
  }

  if (discount.ninetyDays !== undefined) {
    result.push({
      type: EarlyDiscountOptionType.NinetyDays,
      dayType: discount.dayType!,
      days: 90,
      factor: discount.ninetyDays,
    })
  }

  if (discount.custom !== undefined) {
    result.push({
      type: EarlyDiscountOptionType.Custom,
      dayType: discount.dayType!,
      days: discount.customDays || 0,
      factor: discount.custom,
    })
  }

  return result
}

const DealInformationForm = ({ changeStepsAvailable }: Props) => {
  const { hasPermission } = usePermission()
  const s = useDealInformationFormStyles()
  const { push, goBack, location } = useHistory()
  const snack = useSnackbar()
  const { id, view } = useParams<{ id: string; view: 'new' | 'edit' }>()
  const params = new URLSearchParams(location.search)
  const clientId = params.get('clientId')
  const { state } = useContext(UserContext)

  const [isChangingView, setIsChangingView] = useState(false)
  const [isEnabledQuery, setIsEnabledQuery] = useState(true)
  const [selectedIsos, setSelectedIsos] = useState<Array<{ id: number; name: string }>>([])
  const [selectedReps, setSelectedReps] = useState<any[]>([])
  const [representativeState, setRepresentativeState] = useState<any>(null)
  const [selectedClient, setSelectedClient] = useState<IClientListItemLight | null>(null)
  const [customScheduleModalOpen, setCustomScheduleModalOpen] = useState(false)

  const flexPopover = usePopover()
  const factorPopover = usePopover()
  const paymentDayPopover = usePopover()
  const psfPopover = usePopover()
  const positionsPopover = usePopover()
  const externalDealsPopover = usePopover()

  const { isLoading, data } = useQuery(['inhouse-deal'], () => getDeal(id), {
    onSuccess(res) {
      const {
        data: {
          id: dealId,
          client,
          paymentInfo,
          advanceAmount,
          term,
          reverseTerm,
          uccUploaded,
          factor,
          frequentPayment,
          isos,
          representatives,
          comments,
          bankAccount,
          collected,
          sendAsWire,
          company,
          vendors,
          flexAmount,
          repSplitCommission,
          psfFeeSplitCommission,
          extendType,
          merchantPositions,
          externalDeals,
          notificationSubscriptionSettings,
          isSubmitted,
          customSchedule,
          startDate,
          repCommissionTransactionsIsCreated,
          isoCommissionTransactionsIsCreated
        },
      } = res

      if (isos?.length) {
        setSelectedIsos([...selectedIsos.map(x => ({ id: x.id, name: x.name })), ...isos.map(x => ({ id: x.id, name: x.businessName ?? '' }))])
      }

      if (representatives?.length) {
        setSelectedReps([...selectedReps, ...representatives.filter(el => el.id !== 1)])
      }

      const firstCustomPaymentAmount = customSchedule?.payments?.length
        ? [...customSchedule.payments].sort((a, b) => a.order - b.order)[0].amount
        : undefined

      setValues({
        id: dealId,
        clientId: client?.id,
        advanceAmount,
        term,
        reverseTerm,
        factor,
        uccUploaded: Boolean(uccUploaded),
        frequentPayment: firstCustomPaymentAmount ?? frequentPayment,
        isoIds: isos.length > 0 ? isos.map(item => item.id) : undefined,
        representativeIds: representatives && representatives.length > 0 ? representatives.map(item => item.id) : undefined,
        paymentFrequency: customSchedule ? 'Customised' : paymentInfo.paymentFrequency,
        paymentDay: paymentInfo.paymentDay ?? '',
        customPaymentSchedule: customSchedule
          ? {
              frequency: customSchedule.paymentFrequency,
              payments: customSchedule.payments.map(p => ({
                id: p.id,
                order: p.order,
                amount: p.amount,
                percentage: 0,
                isProcessed: p.isProcessed,
                scheduledDate: p.scheduledDate
              })),
            }
          : undefined,
        disbursementFrequency: paymentInfo.disbursementFrequency,
        paybackAmount: paymentInfo.payback,
        commissionToRep: paymentInfo.commissionToRep,
        repSplitCommission: repSplitCommission,
        isoSplitCommission: isos.map(x => ({ id: x.id, value: new Decimal(x.amountValue) })),
        psfFeeSplitCommission: psfFeeSplitCommission,
        repCommissionDueDate: paymentInfo.repCommissionDueDate,
        isoCommissionDueDate: paymentInfo.isoCommissionDueDate,
        isoFee: paymentInfo.isoFee,
        psfFee: paymentInfo.psfFee,
        collected: collected,
        syndicationFee: paymentInfo.syndicationFee,
        payOffAmount: paymentInfo.payOffAmount,
        comments: comments ?? '',
        commissionSplit: paymentInfo.commissionSplit,
        sendAsWire,
        bankAccountId: bankAccount.id,
        companyId: company?.id,
        vendorIds: vendors?.map(v => v.id),
        flexAmount: flexAmount,
        isFlexDeal: flexAmount != null,
        earlyPayOffAmount: paymentInfo.earlyPayOffAmount,
        earlyDiscount: paymentInfo.earlyDiscount,
        earlyDiscountOptions: paymentInfo.earlyDiscountOptions,
        extendType: extendType,
        specifiedPercentage: paymentInfo.specifiedPercentage ?? undefined,
        apr: paymentInfo.apr ?? undefined,
        monthlyRevenue: paymentInfo.monthlyRevenue ?? undefined,
        clientState: undefined,
        flexSchedule: values.flexSchedule ?? undefined,
        merchantPositions: merchantPositions ?? [],
        externalDeals: externalDeals ?? [],
        syndicationFeeDistribution: paymentInfo.syndicationFeeDistribution,
        notificationSubscriptionSettings:
          notificationSubscriptionSettings.length > 0 ? notificationSubscriptionSettings : defaultNotificationSettings,
        isRoundingEnabled: paymentInfo.isRoundingEnabled,
        isSubmitted: isSubmitted ?? false,
        isRefiDeal: Boolean(res.data.mainDealId),
        startDate: startDate,
        isoCommissionTransactionsIsCreated: isoCommissionTransactionsIsCreated,
        repCommissionTransactionsIsCreated: repCommissionTransactionsIsCreated,
        firstPaymentDate: paymentInfo.firstPaymentDate,
        initialStartDate: startDate,
        initialFirstPaymentDate: paymentInfo.firstPaymentDate,
        initialFlexSchedule: values.initialFlexSchedule ?? undefined,
        initialIsoCommissionDueDate: paymentInfo.isoCommissionDueDate,
        initialRepCommissionDueDate: paymentInfo.repCommissionDueDate
      })
      setIsEnabledQuery(false)
      setSelectedClient(
        client
          ? {
              id: client.id.toString(),
              businessName: client.businessName,
              physicalStateId: client.phisicalStateId,
            }
          : null
      )
    },
    onError() {
      snack.enqueueSnackbar(<Typography>Something went wrong</Typography>)
    },
    cacheTime: 0,
    enabled: id && isEnabledQuery,
  })

  const { data: repData } = useQuery(['rep'], () => getRepLight({ PageSize: 4000 }), {
    onSuccess(response) {
      const houseRep = response.data.find(rep => rep.id === 1)

      if (selectedReps.find(el => el?.id === houseRep?.id)) return

      setSelectedReps([...selectedReps, houseRep])
      setRepresentativeState(houseRep)
    },
    enabled: !state.roles.isISO,
  })

  const { data: isoData } = useQuery(['iso'], () => getIsoLight({ PageSize: 4000 }), {
    enabled: !!id,
  })

  const isApprovedDeal = useMemo(() => {
    return Boolean(data?.data.status === DealsStatus.approved)
  }, [data])

  const isFullyPaidDeal = useMemo(() => {
    return data?.data.paidStatus === 'FullyPaid'
  }, [data])

  const isRepCommissionDueDateDisabled = useMemo(() => {
    const deal = data?.data

    return deal && isApprovedDeal && Boolean(deal.paymentInfo.commissionToRep) 
      && deal.repCommissionTransactionsIsCreated
  }, [data])

  const isIsoCommissionDueDateDisabled = useMemo(() => {
    const deal = data?.data

    return deal && isApprovedDeal && Boolean(deal.isos.length) 
      && deal.isoCommissionTransactionsIsCreated
  }, [data])

  const isDisabledToEdit = (data?.data.status === 'Declined' || data?.data.status === 'Approved') && !state.roles.isAdmin
  const isDisabledSplitCommission = !(state.roles.isFunder || state.roles.isAdmin)
  const isDisabledEarlyPayoff = !isApprovedDeal || isDisabledSplitCommission
  const isDisabledFactorRate = data?.data.status === DealsStatus.approved || isDisabledToEdit

  const isRepClawbackSent = data?.data.repClawbackSent
  const isIsoClawbackSent = data?.data.isoClawbackSent
  const isDisabledRepSelection = isDisabledToEdit || isRepClawbackSent
  const isDisabledIsoSelection = isDisabledToEdit || isIsoClawbackSent

  const [mutateCreateDeal, { isLoading: isCreateLoading }] = useMutation(createInhouseDeal, {
    onSuccess(res) {
      changeStepsAvailable([0, 1, 2])
      push(
        generatePath(NEW_DEAL_URL, {
          id: res.data.id,
          view: 'edit',
          type: 'inhouse',
          step: 2,
        })
      )
      snack.enqueueSnackbar(<Typography>Successfully created deal</Typography>)
    },
    onError(err: AxiosError) {
      const errorData = err.response?.data as { description?: string } | undefined
      const dateErrorMessage = getDateErrorMessage(errorData?.description)
      if (errorData?.description === 'ADVANCE_AMOUNT_MUST_BE_HIGHER_THAN_PRIOR_BALANCE')
        snack.enqueueSnackbar('Advance amount must be higher than the total prior balance')
      else if (errorData?.description === 'NET_AMOUNT_IS_NEGATIVE') snack.enqueueSnackbar('Net amount is negative')
      else if (dateErrorMessage) snack.enqueueSnackbar(dateErrorMessage, { 
        variant: 'error',
        autoHideDuration: 10000
      })
      else snack.enqueueSnackbar(<Typography>Something went wrong</Typography>)
    },
  })

  const [mutateUpdateDeal, { isLoading: isUpdateLoading }] = useMutation(editInhouseDeal, {
    onSuccess() {
      queryCache.invalidateQueries(['inhouse-deal', id])

      if (isApprovedDeal) {
        push(
          generatePath(DEAL_INFO_URL, {
            id: id,
          })
        )
      } else
        push(
          generatePath(NEW_DEAL_URL, {
            id: id,
            view: 'edit',
            type: 'inhouse',
            step: 2,
          })
        )
      snack.enqueueSnackbar('Successfully updated deal')
    },
    onError(err: AxiosError) {
      const errorData = err.response?.data as { description?: string } | undefined
      const dateErrorMessage = getDateErrorMessage(errorData?.description)
      if (errorData?.description === 'ADVANCE_AMOUNT_MUST_BE_HIGHER_THAN_PRIOR_BALANCE'
        || (errorData?.description === 'INVALID_ADVANCE_AMOUNT_VALUE' && data?.data.mainDealId))
        snack.enqueueSnackbar('Advance amount must be higher than the total prior balance')
      else if (errorData?.description === 'NET_AMOUNT_IS_NEGATIVE') snack.enqueueSnackbar('Net amount is negative')
      else if (errorData?.description === 'INVALID_FUND_AMOUNT') snack.enqueueSnackbar(LENDER_DONT_HAVE_ENOUGHT_MONEY)
      else if (errorData?.description === 'INVALID_SPLIT_COMMISSION_VALUE') snack.enqueueSnackbar(SPLIT_COMMISSION_ERROR)
      else if (dateErrorMessage) snack.enqueueSnackbar(dateErrorMessage, { 
        variant: 'error',
        autoHideDuration: 10000
      })
      else snack.enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const {
    values,
    errors,
    touched,
    submitCount,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setValues,
    resetForm,
  } = useFormik<InhouseDealForm>({
    validationSchema: schemaInHouseDeal,
    initialValues: {
      companyId: undefined,
      id: undefined,
      clientId: parseInt(clientId || '') || undefined,
      paymentFrequency: '',
      paymentDay: '',
      disbursementFrequency: undefined,
      advanceAmount: undefined,
      term: undefined,
      reverseTerm: undefined,
      uccUploaded: true,
      factor: undefined,
      frequentPayment: undefined,
      paybackAmount: undefined,
      isoIds: undefined,
      commissionToRep: undefined,
      isoFee: undefined,
      commissionSplit: undefined,
      repSplitCommission: undefined,
      isoSplitCommission: [],
      psfFeeSplitCommission: undefined,
      repCommissionDueDate: undefined,
      isoCommissionDueDate: undefined,
      representativeIds: undefined,
      psfFee: undefined,
      syndicationFee: undefined,
      payOffAmount: undefined,
      sendAsWire: undefined,
      comments: '',
      bankAccountId: undefined,
      vendorIds: undefined,
      flexAmount: undefined,
      flexSchedule: undefined,
      isFlexDeal: false,
      earlyPayOffAmount: undefined,
      earlyDiscount: undefined,
      earlyDiscountOptions: undefined,
      specifiedPercentage: undefined,
      apr: undefined,
      monthlyRevenue: undefined,
      clientState: undefined,
      merchantPositions: [],
      externalDeals: [],
      syndicationFeeDistribution: undefined,
      notificationSubscriptionSettings: defaultNotificationSettings,
      isRoundingEnabled: false,
      isSubmitted: false,
      isRefiDeal: false,
      customPaymentSchedule: undefined,
      startDate: getCurrentDate().toISOString(),
      isoCommissionTransactionsIsCreated: undefined,
      repCommissionTransactionsIsCreated: undefined,
      firstPaymentDate: getCurrentDate().toISOString(),
      initialStartDate: getCurrentDate().toISOString(),
      initialFirstPaymentDate: getCurrentDate().toISOString(),
      initialFlexSchedule: undefined,
      initialIsoCommissionDueDate: undefined,
      initialRepCommissionDueDate: undefined
    },
    enableReinitialize: false,
    onSubmit: formValues => {
      const transformedValues = Object.entries(formValues).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]:
            value ||
            ((key === 'syndicationFee' ||
              key === 'payOffAmount' ||
              key === 'earlyPayOffAmount' ||
              key === 'psfFee' ||
              key === 'isoFee' ||
              key === 'commissionToRep') &&
              '0') ||
            (key === 'comments' && '') ||
            null,
          uccUploaded: formValues.uccUploaded,
          sendAsWire: formValues.sendAsWire,
          isRoundingEnabled: formValues.isRoundingEnabled,
          flexAmount: formValues.isFlexDeal ? formValues.flexAmount : null,
          paymentDay: formValues.paymentFrequency === 'Weekly' ? formValues.paymentDay : null,
          isoSplitCommission: formValues.isoSplitCommission.map(commision => {
            return {
              id: commision.id,
              value: commision.value.toString(),
            }
          }),
        }),
        {}
      ) as ICreateUpdateInhouseDeal

      let payload: ICreateUpdateInhouseDeal = transformedValues
      if (formValues.paymentFrequency === 'Customised' && formValues.customPaymentSchedule) {
        const schedule = formValues.customPaymentSchedule
        payload = {
          ...transformedValues,
          paymentFrequency: schedule.frequency,
          customPaymentSchedule: undefined,
          customSchedule: {
            paymentFrequency: schedule.frequency,
            payments: schedule.payments.map(p => ({ amount: p.amount, order: p.order, scheduledDate: p.scheduledDate })),
          },
        } as ICreateUpdateInhouseDeal & {
          customSchedule: { paymentFrequency: string; payments: { amount: number; order: number }[];}
        }
      }

      if (id) {
        mutateUpdateDeal(payload)
      } else {
        mutateCreateDeal(payload)
      }
    },
  })

  const getInitialValuesForIsoCommission = useCallback((): { [key: number]: Decimal } => {
    const initialValues: { [key: number]: Decimal } = {}
    const isoFeeAmount = Number(values.isoFee || 0)

    if (values.isoSplitCommission.length === 0) {
      const totalIsos = selectedIsos.length
      if (totalIsos > 0) {
        const baseValue = bankRounding(isoFeeAmount / totalIsos, DECIMAL_PLACES)
        let distributed = 0

        selectedIsos.forEach((iso, index) => {
          if (index === totalIsos - 1) {
            initialValues[iso.id] = bankRoundingDecimal(isoFeeAmount - distributed, DECIMAL_PLACES)
          } else {
            initialValues[iso.id] = bankRoundingDecimal(baseValue, DECIMAL_PLACES)
            distributed += baseValue
          }
        })
      }
    } else {
      selectedIsos.forEach(iso => {
        const match = values.isoSplitCommission.find(x => x.id === iso.id)
        initialValues[iso.id] = bankRoundingDecimal(match ? match.value.toNumber() : 0, DECIMAL_PLACES)
      })
    }

    return initialValues
  }, [selectedIsos, values.isoSplitCommission, values.isoFee])

  const getInitialValuesForCommissionLocal = useCallback(
    (name: 'repSplitCommission' | 'psfFeeSplitCommission') => {
      const feeAmount = name === 'repSplitCommission' ? Number(values.commissionToRep || 0) : Number(values.psfFee || 0)
      const assignAllToMainRep = name === 'psfFeeSplitCommission'
      return getInitialValuesForCommission(selectedReps, values?.[name], feeAmount, assignAllToMainRep)
    },
    [selectedReps, values]
  )

  useQuery(['flexSchedule', id], () => getFlexScheduleByDeal(id), {
    onSuccess({ data: flexSchedule }) {
      setValues(currentValues => ({
        ...currentValues,
        flexSchedule: [...flexSchedule],
        initialFlexSchedule: [...flexSchedule],
      }))
    },
    retry: false,
    cacheTime: 0,
    enabled: Boolean(id) && !isEnabledQuery,
  })

  const flexDisbursementFormValues: IFlexDisbursementForm = useMemo(() => {
    return {
      schedule: values.flexSchedule ?? [],
      advanceAmount: values.advanceAmount || 0,
      flexAmount: values.flexAmount || 0,
    }
  }, [values.flexSchedule, values.advanceAmount, values.flexAmount])

  const flexScheduleError = useMemo(() => {
    if (!Array.isArray(errors.flexSchedule)) return undefined

    const firstError = errors.flexSchedule.find(Boolean)
    return typeof firstError === 'string' ? firstError : firstError?.date
  }, [errors.flexSchedule])

  const { data: statesData } = useQuery('states', getStates, {
    onSuccess(res) {
      if (data?.data?.client?.phisicalStateId) {
        setFieldValue('clientState', res.data?.find(x => x.id === data?.data?.client?.phisicalStateId)?.abbreviation)
      }
    },
    retry: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    enabled: view === 'new' || Boolean(data?.data?.client?.phisicalStateId),
  })

  const { data: dataBankAccounts } = useQuery(
    ['client-bank-accounts', values.clientId],
    () =>
      getClientBankAccounts({
        clientId: values.clientId ? values.clientId : 0,
      }),
    {
      cacheTime: 0,
      retry: false,
      enabled: values.clientId,
    }
  )

  const [reverseSchedule, setReverseSchedule] = useState<IFlexDisbursement[]>()
  const [debouncedMerchantPositions, setDebouncedMerchantPositions] = useState(values.merchantPositions)
  useDebouncedEffect(() => setDebouncedMerchantPositions(values.merchantPositions), [2000, values.merchantPositions])
  const { isLoading: isLoadingReverse } = useQuery(
    ['reverseSchedule', debouncedMerchantPositions, values.disbursementFrequency, values.startDate],
    () =>
      calculateReverseSchedule(
        values.disbursementFrequency ?? PaymentFrequencyEnum.Weekly,
        debouncedMerchantPositions,
        values.startDate
      ),
    {
      onSuccess: ({ data: schedule }) => setReverseSchedule(schedule),
      enabled: !isApprovedDeal && values.extendType === 'Reverse' && values.merchantPositions.length && values.disbursementFrequency,
    }
  )

  const generateCommissionData = (name: 'repSplitCommission' | 'psfFeeSplitCommission') => {
    const commissionData = []
    const initialValuesForCommission = getInitialValuesForCommissionLocal(name)

    if (!initialValuesForCommission) return

    for (const [key, value] of Object.entries(initialValuesForCommission)) {
      const numberKey = Number(key)
      const numberValue = Number(value)

      commissionData.push({ id: numberKey, value: numberValue })
    }

    return commissionData
  }

  const generateIsoCommissionData = () => {
    const commissionData = []
    const initialValuesForCommission = getInitialValuesForIsoCommission()

    if (!initialValuesForCommission) return

    for (const [key, value] of Object.entries(initialValuesForCommission)) {
      const numberKey = Number(key)

      commissionData.push({ id: numberKey, value: value })
    }

    return commissionData
  }

  const [editingPosition, setEditingPosition] = useState<{
    event?: React.MouseEvent<HTMLButtonElement>
    position?: IMerchantPosition
    deleteId?: number
  }>()

  const getNewPositionId = () => values.merchantPositions.reduce((min, obj) => Math.min(min, obj.id), 0) - 1
  const handleAddMerchantPosition = (p: IMerchantPosition) => {
    setFieldValue('merchantPositions', [...values.merchantPositions, { ...p, id: getNewPositionId() }])
  }

  const handleRemoveMerchantPosition = (i: number) => {
    setFieldValue(
      'merchantPositions',
      values.merchantPositions.filter(p => p.id !== i)
    )
  }

  const handleEditMerchantPosition = (p: IMerchantPosition) => {
    const otherPositions = values.merchantPositions.filter(position => position.id !== p.id)
    setFieldValue('merchantPositions', [...otherPositions, { ...p, id: getNewPositionId() }])
  }

  const clearMerchantPositions = () => {
    setFieldValue('merchantPositions', [])
  }

  const clearCommissionDueDates = () => {
    setFieldValue('isoCommissionDueDate', undefined)
    setFieldValue('repCommissionDueDate', undefined)
  }

  const [editingExternalDeal, setEditingExternalDeal] = useState<{
    event?: React.MouseEvent<HTMLButtonElement>
    externalDeal?: IExternalDeal
    deleteId?: number
  }>()

  const getNewExternalDealId = () => values.externalDeals.reduce((min, obj) => Math.min(min, obj.id), 0) - 1
  const handleAddExternalDeal = (ed: IExternalDeal) => {
    const newDeals = [...values.externalDeals, { ...ed, id: getNewExternalDealId() }]
    setFieldValue('externalDeals', newDeals)
    const externalPayoff = newDeals.reduce((sum, d) => sum + (Number(d.payoffBalance) || 0), 0)
    const parentRemaining = data?.data.mainDealId ? Number(values.payOffAmount) || 0 : 0
    const totalPayoff = externalPayoff + parentRemaining
    if (totalPayoff > 0 && (!values.advanceAmount || Number(values.advanceAmount) < totalPayoff + 1)) {
      setFieldValue('advanceAmount', totalPayoff + 1)
    }
  }

  const handleRemoveExternalDeal = (i: number) => {
    setFieldValue(
      'externalDeals',
      values.externalDeals.filter(ed => ed.id !== i)
    )
  }

  const handleEditExternalDeal = (ed: IExternalDeal) => {
    const otherDeals = values.externalDeals.filter(d => d.id !== ed.id)
    const newDeals = [...otherDeals, { ...ed, id: getNewExternalDealId() }]
    setFieldValue('externalDeals', newDeals)
    const externalPayoff = newDeals.reduce((sum, d) => sum + (Number(d.payoffBalance) || 0), 0)
    const parentRemaining = data?.data.mainDealId ? Number(values.payOffAmount) || 0 : 0
    const totalPayoff = externalPayoff + parentRemaining
    if (totalPayoff > 0 && (!values.advanceAmount || Number(values.advanceAmount) < totalPayoff + 1)) {
      setFieldValue('advanceAmount', totalPayoff + 1)
    }
  }

  const clearExternalDeals = () => {
    setFieldValue('externalDeals', [])
  }

 const handleNotificationPreferencesChanged = useCallback(
    (notificationType: DealNotificationType, preferences: INotificationSubscriptionPreferences | null) => {
      setValues(currentValues => {
        const updatedSettings = currentValues.notificationSubscriptionSettings.filter(
          x => x.notificationType !== notificationType
        )

        if (preferences !== null) {
          updatedSettings.push({ ...preferences, notificationType })
        }

        return {
          ...currentValues,
          notificationSubscriptionSettings: updatedSettings
        }
      })
    },
    [setValues]
  )

  const handlePaymentFrequencyChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const newFrequency = e.target.value as string
    handleChange(e)

    if (newFrequency !== 'Weekly') {
      setFieldValue('paymentDay', '')
    } else if (!values.paymentDay) {
      setFieldValue('paymentDay', getPaymentDayFromDate(values.firstPaymentDate))
    }

    if (newFrequency === 'Customised') {
      if (!values.factor || !values.advanceAmount || !values.startDate || !values.firstPaymentDate) {
        snack.enqueueSnackbar(
          'Please fill in the Factor rate, Advance Amount, Deal start date or/and First payment date field.', 
          { variant: 'error' })
        setFieldValue('paymentFrequency', '')
        return
      }
      setFieldValue('term', '')
    } else {
      setFieldValue('customPaymentSchedule', undefined)
    }
  }

  const handleOpenCustomScheduleModal = () => {
    setCustomScheduleModalOpen(true)
  }

  const handleCloseCustomScheduleModal = () => {
    setCustomScheduleModalOpen(false)
  }

  const handleSaveCustomSchedule = (schedule: ICustomPaymentSchedule, firstPaymentDate: string) => {
    setFieldValue('customPaymentSchedule', schedule)
    setFieldValue('term', String(schedule.payments.length))
    setFieldValue('firstPaymentDate', firstPaymentDate)
    
    const firstPayment = [...schedule.payments].sort((a, b) => a.order - b.order)[0]
    if (firstPayment) {
      setFieldValue('frequentPayment', firstPayment.amount)
    }
    setCustomScheduleModalOpen(false)
  }

  const handleCustomScheduleError = (message: string) => {
    snack.enqueueSnackbar(message, { variant: 'error' })
  }

  const firstPaymentDateShouldBeUpdated = useCallback((value: Date): boolean => {
    return !!(values.firstPaymentDate && isBeforeDate(values.firstPaymentDate, value))
  }, [values.firstPaymentDate])

  const isoCommissionDueDateShouldBeUpdated = useCallback((value: Date): boolean => {
    return !!(values.isoCommissionDueDate && isBeforeDate(values.isoCommissionDueDate, value))
  }, [values.isoCommissionDueDate])

  const repCommissionDueDateShouldBeUpdated = useCallback((value: Date): boolean => {
    return !!(values.repCommissionDueDate && isBeforeDate(values.repCommissionDueDate, value))
  }, [values.repCommissionDueDate])

  const applyFormUpdates = useCallback((updates: Partial<InhouseDealForm>) => {
    let newSchedule = values.customPaymentSchedule;

    //if the updates payload includes a new firstPaymentDate, recalculate the schedule
    if (updates.firstPaymentDate && newSchedule) {
      const updatedPayments = newSchedule.payments.map((payment, index) => ({
        ...payment,
        scheduledDate: calculateCustomScheduleDate(index, updates.firstPaymentDate, newSchedule!.frequency)
      }));
      newSchedule = { ...newSchedule, payments: updatedPayments };
    }

    //apply everything to Formik in one batch
    setValues({
      ...values,
      ...updates,
      customPaymentSchedule: newSchedule
    });
  }, [values, setValues]);

  const handleStartDateChange = useCallback((value: MaterialUiPickersDate) => {
    const converted = convertDateForPicker(value) as Date | null;

    if (converted && isValidDate(converted)) {
      const newIsoDate = converted.toISOString();
      const needsUpdateFirstPaymentDate = firstPaymentDateShouldBeUpdated(converted);
      const needsUpdateIsoCommissionDueDate = isoCommissionDueDateShouldBeUpdated(converted);
      const needsUpdateRepCommissionDueDate = repCommissionDueDateShouldBeUpdated(converted);

      applyFormUpdates({
        startDate: newIsoDate,
        //only include if it needs to update
        ...(needsUpdateFirstPaymentDate && { firstPaymentDate: newIsoDate }), 
        ...(needsUpdateIsoCommissionDueDate && { isoCommissionDueDate: newIsoDate }),
        ...(needsUpdateRepCommissionDueDate && { repCommissionDueDate: newIsoDate })
      });

      //show warning to user that commission due dates are changed automatically 
      if (needsUpdateIsoCommissionDueDate || needsUpdateRepCommissionDueDate) {
        snack.enqueueSnackbar(COMMISION_DUE_DATE_WARNING, 
        { 
          variant: 'warning',
          autoHideDuration: 6000
        })
      }
    } else {
      setFieldValue('startDate', undefined);
    }
  }, 
[applyFormUpdates, setFieldValue, snack, firstPaymentDateShouldBeUpdated, 
  isoCommissionDueDateShouldBeUpdated, repCommissionDueDateShouldBeUpdated]);

const handleFirstPaymentDateChange = useCallback((value: MaterialUiPickersDate) => {
  const converted = convertDateForPicker(value) as Date | null;

  if (converted && isValidDate(converted)) {
    const newIsoDate = converted.toISOString();

    applyFormUpdates({
        firstPaymentDate: newIsoDate,
    });

  } else {
    setFieldValue('firstPaymentDate', undefined); 
  }
}, [setFieldValue, applyFormUpdates]);

const handleSubmitFlexDisbursement = useCallback(({ schedule, dealStartDate }: IFlexDisbursementFormValues) => {
  flexPopover.close();
  
  if (dealStartDate) {
    const converted = new Date(dealStartDate);
    const newIsoDate = converted.toISOString();
    const needsUpdateFirstPaymentDate = firstPaymentDateShouldBeUpdated(converted);

    applyFormUpdates({
      flexSchedule: schedule,
      startDate: newIsoDate,
      //only include if it needs to update
      ...(needsUpdateFirstPaymentDate && { firstPaymentDate: newIsoDate }), 
    });

    //show warning to user that first pyament date is changed automatically 
    if (needsUpdateFirstPaymentDate) {
      snack.enqueueSnackbar(FIRST_PAYMENT_DATE_WARNING, 
      { 
        variant: 'warning', 
        autoHideDuration: 6000 
      });
    }
  } else {
    applyFormUpdates({
      flexSchedule: schedule,
      startDate: undefined
    });
  }
}, [applyFormUpdates, flexPopover, snack, firstPaymentDateShouldBeUpdated]);

  // const handleStartDateChange = useCallback((value: MaterialUiPickersDate) => {
  //   const converted = convertDateForPicker(value) as Date | null
    
  //     if (converted && isValidDate(converted)) {
  //       setFieldValue('startDate', converted.toISOString()) 
  //       tryUpdateRelatedDates(converted)
  //     } else {
  //       setFieldValue('startDate', undefined); 
  //     }
  //   }, [values.customPaymentSchedule?.firstPaymentDate, values.isoCommissionDueDate, values.repCommissionDueDate])

// const handleSubmitFlexDisbursement = useCallback(({ schedule, dealStartDate }: IFlexDisbursementFormValues) => {
//   flexPopover.close();
//   setFieldValue('flexSchedule', schedule);
//   setFieldValue('startDate', dealStartDate ?? undefined);

//   if(dealStartDate){
//     tryUpdateRelatedDates(new Date(dealStartDate))
//   }
// }, [values.customPaymentSchedule?.firstPaymentDate, values.isoCommissionDueDate, values.repCommissionDueDate]);

//   const tryUpdateRelatedDates = useCallback((value: Date) => {
//     const schedule = values.customPaymentSchedule
//     const date = value.toISOString()

//     if (schedule && schedule.firstPaymentDate && isBefore(new Date(schedule.firstPaymentDate), new Date(value))) {
//       const freq = schedule.frequency

//       const updatedPayments = schedule.payments.map((payment, index) => {
//         const newDate = calculateCustomScheduleDate(index, date, freq)
//         return { ...payment, scheduledDate: newDate }
//       });

//       setFieldValue('customPaymentSchedule', {
//         ...schedule,
//         firstPaymentDate: date,
//         payments: updatedPayments
//       });

//       snack.enqueueSnackbar(FIRST_PAYMENT_DATE_WARNING, 
//       { 
//         variant: 'warning',
//         autoHideDuration: 6000
//       })
//     }

//     let commissionDueDateWarning = false

//     if (values.isoCommissionDueDate && isBeforeDate(values.isoCommissionDueDate, value)) {
//       setFieldValue('isoCommissionDueDate', date)
//       commissionDueDateWarning = true
//     }

//     if (values.repCommissionDueDate && isBeforeDate(values.repCommissionDueDate, value)) {
//       setFieldValue('repCommissionDueDate', date)
//       commissionDueDateWarning = true
//     }

//     if(commissionDueDateWarning){
//       snack.enqueueSnackbar(COMMISION_DUE_DATE_WARNING, 
//       { 
//         variant: 'warning',
//         autoHideDuration: 6000
//       })
//     }

//   }, [values.customPaymentSchedule?.firstPaymentDate, values.isoCommissionDueDate, values.repCommissionDueDate])

  useEffect(() => {
    if (editingPosition?.position && editingPosition.event) positionsPopover.open(editingPosition.event)
  }, [editingPosition])

  useEffect(() => {
    if (editingExternalDeal?.externalDeal && editingExternalDeal.event) externalDealsPopover.open(editingExternalDeal.event)
  }, [editingExternalDeal])

  useDebouncedEffect(() => {
    if (values.extendType !== 'Reverse' || !reverseSchedule) return
    const bankFee = Number(values.syndicationFee || 0)
    const payoffTotal = reverseSchedule.reduce((acc, x) => acc + (x.amount ?? 0), 0)
    const advance = bankRounding(payoffTotal + bankFee, DECIMAL_PLACES)
    const isBankFeeDistributed = values.syndicationFeeDistribution === 'Distribute' && payoffTotal > 0

    const grossSchedule = reverseSchedule.map(disbursement => ({
      ...disbursement,
      amount: isBankFeeDistributed
        ? bankRounding((disbursement.amount ?? 0) * (1 + bankFee / payoffTotal), DECIMAL_PLACES)
        : disbursement.amount,
    }))
    const subsequentSchedule = grossSchedule.slice(1)
    const flexAmount = bankRounding(
      advance - subsequentSchedule.reduce((acc, x) => acc + (x.amount ?? 0), 0),
      DECIMAL_PLACES
    )

    setFieldValue('advanceAmount', advance || '')
    setFieldValue('flexAmount', flexAmount || '')
    setFieldValue('reverseTerm', reverseSchedule.length || '')
    setFieldValue('flexSchedule', subsequentSchedule)
  }, [500, values.extendType, reverseSchedule, values.syndicationFee, values.syndicationFeeDistribution])

  // Keep Reverse flex amount in sync with schedule and bank fee to avoid validation drift
  useEffect(() => {
    if (values.extendType !== 'Reverse') return
    const totalSchedule = (values.flexSchedule || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
    const advance = Number(values.advanceAmount) || 0
    const recalculatedFlex = bankRounding(advance - totalSchedule, DECIMAL_PLACES)

    if (Number(values.flexAmount || 0) !== recalculatedFlex) {
      setFieldValue('flexAmount', recalculatedFlex)
    }
  }, [values.extendType, values.syndicationFee, values.flexSchedule, values.advanceAmount])

  useEffect(() => {
    if (!values.isFlexDeal) {
      setFieldValue('flexAmount', '')
      setFieldValue('extendType', '')
    }
    // Once a deal is FullyPaid, PaybackAmount may have been mutated down to a discounted value
    // (advance * early-payoff factor). Re-deriving it from advance * factor would clobber that
    // and silently flip the deal back out of FullyPaid on the next save.
    if (isFullyPaidDeal) {
      return
    }
    if (values.advanceAmount !== undefined && values.factor !== undefined) {
      setFieldValue('paybackAmount', bankRounding(values.advanceAmount * Number(values.factor || 0), DECIMAL_PLACES))
    }
    if (values.paymentFrequency === 'Customised') {
      return
    }
    if (values.advanceAmount !== undefined && values.term !== undefined) {
      let frequentPayment = bankRoundingAwayFromZero((values.advanceAmount * Number(values.factor || 0)) / Number(values.term || 1), DECIMAL_PLACES)

      if (values.isRoundingEnabled) {
        frequentPayment = Math.ceil(frequentPayment)
      }

      setFieldValue('frequentPayment', frequentPayment)
    }
  }, [
    isFullyPaidDeal,
    values.isFlexDeal,
    values.flexAmount,
    values.advanceAmount,
    values.factor,
    values.term,
    values.isRoundingEnabled,
    values.paymentFrequency,
  ])

  useEffect(() => {
    const { advanceAmount, factor, syndicationFee, term, paymentFrequency: frequency } = values

    if (advanceAmount && factor && term && frequency) {
      const advanceAmountNum = Number(advanceAmount)
      const paybackAmountNum = advanceAmount * factor
      const bankFee = Number(syndicationFee || 0)
      const termNum = Number(term)

      // Net Funded Amount = Advance Amount - Bank fee
      const netFundedAmount = advanceAmountNum - bankFee

      // Total Cost of Funds = Payback Amount - Net Funded Amount
      const totalCostOfFunds = paybackAmountNum - netFundedAmount

      if (netFundedAmount > 0) {
        // Convert term to actual calendar days based on payment frequency
        let termInDays: number

        switch (frequency.toLowerCase()) {
          case 'daily':
            termInDays = termNum
            break
          case 'weekly':
            termInDays = termNum * 7
            break
          case 'biweekly':
          case 'bi-weekly':
            termInDays = termNum * 14
            break
          default:
            termInDays = termNum
        }

        const apr = (totalCostOfFunds / netFundedAmount) * (365 / termInDays) * 100
        const roundedApr = bankRounding(apr, DECIMAL_PLACES_PERCENTAGE)

        setFieldValue('apr', roundedApr)
      }
    }
  }, [values.advanceAmount, values.factor, values.syndicationFee, values.term, values.paymentFrequency, setFieldValue])

  useEffect(() => {
    // Only auto-set uccUploaded when user manually changes advanceAmount (not when it's recalculated from Bank Fee in Reverse deals)
    if (touched.advanceAmount && values.advanceAmount && values.extendType !== 'Reverse') {
      setFieldValue('uccUploaded', values.advanceAmount >= 20000)
    }
  }, [values.advanceAmount, values.extendType])

  useEffect(() => {
    setIsChangingView(true)
    resetForm()
    setTimeout(() => {
      setIsChangingView(false)
    }, 10)
  }, [view])

  const totalCommission = useMemo(() => {
    return Number(values.isoFee || 0) + Number(values.commissionToRep || 0)
  }, [values.isoFee, values.commissionToRep])

  const notificationSettings = useMemo(() => {
    return defaultNotificationSettings.concat(defaultRolelessNotificationSettings)
  }, [])

  if (isChangingView) {
    return null
  }

  if (isLoading) {
    return (
      <Box display="flex" height="400px" width="100%" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    )
  }

  if (data && data.data.type === CompanyType.OutHouse)
    push(
      generatePath(DEAL_INFO_URL, {
        id: id,
      })
    )

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      component="form"
      onSubmit={(e: any) => {
        setFieldValue('repSplitCommission', generateCommissionData('repSplitCommission'))
        setFieldValue('psfFeeSplitCommission', generateCommissionData('psfFeeSplitCommission'))
        setFieldValue('isoSplitCommission', generateIsoCommissionData())

        handleSubmit(e)
      }}
      className={s.formContainer}
    >
      <Paper className={s.paper}>
        <Typography className={s.heading}>{id ? 'Edit deal' : 'Add New Deal'}</Typography>
        {view === 'new' && (
          <SwitchExtended
            variants={[
              { title: 'Inhouse Deal', value: 'inhouse' },
              { title: 'Outhouse Deal', value: 'outhouse' },
            ]}
            value="inhouse"
            className={s.switch}
            onClick={() =>
              push({
                pathname: '/deal/new/outhouse/1',
                search: location.search,
              })
            }
          />
        )}

        <Grid container spacing={8}>
          <Grid item xs={6}>
            <ClientAutoComplete
              showVerificationStatus
              disabled={isDisabledToEdit}
              label={<RequiredOption label="Choose the client" />}
              error={Boolean(touched.clientId && errors.clientId)}
              helperText={touched.clientId && errors.clientId}
              clientId={values.clientId}
              onChange={(e, value: IClientListItemLight) => {
                handleChange({ target: { name: 'clientId', value: value?.id || '' } })
                setFieldValue('bankAccountId', '')
                setFieldValue('clientState', statesData?.data?.find(x => x.id === value?.physicalStateId)?.abbreviation)
                setSelectedClient(value)
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <CompanyAutoComplete
              disabled={isDisabledToEdit}
              label={<RequiredOption label="Company" />}
              error={Boolean(touched.companyId && errors.companyId)}
              helperText={touched.companyId && errors.companyId}
              onChange={(e, value) => {
                if (value) setFieldValue('companyId', value.id)
                else {
                  setFieldValue('companyId', undefined)
                }
              }}
              companyId={values.companyId}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectComponent
              disabled={isDisabledToEdit || isApprovedDeal}
              name="paymentFrequency"
              label={<RequiredOption label="Payment Frequency" />}
              onChange={handlePaymentFrequencyChange}
              labelProps={{ className: s.selectLabel }}
              fullWidth
              error={Boolean(touched.paymentFrequency && errors.paymentFrequency)}
              helperText={(touched.paymentFrequency && errors.paymentFrequency) || (touched.paymentDay && errors.paymentDay)}
              value={values.paymentFrequency}
              className={values.paymentFrequency === 'Weekly' ? s.selectWithAdornment : undefined}
              endAdornment={
                values.paymentFrequency === 'Weekly' ? (
                  <InputAdornment position="end" className={s.paymentDayAdornment}>
                    <IconButton className={s.paymentDayButton} onClick={paymentDayPopover.open}>
                      {values.paymentDay ? paymentDayShortLabels[values.paymentDay as PaymentDayEnum] : <CalendarIcon />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
            >
              {paymentFrequency.map(item => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </SelectComponent>
            <Popover
              open={paymentDayPopover.isOpen}
              anchorEl={paymentDayPopover.anchorEl}
              onClose={paymentDayPopover.close}
              PaperProps={{ classes: { root: s.paymentDayPopover } }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <PaymentDaySelector
                disabled={isDisabledToEdit || (isApprovedDeal && Boolean(data?.data.paymentInfo?.paymentDay))}
                value={values.paymentDay}
                onChange={value => {
                  setFieldValue('paymentDay', value)
                  setFieldTouched('paymentDay')
                  paymentDayPopover.close()
                }}
              />
            </Popover>
          </Grid>
          <Grid item xs={6}>
            <DecimalTextField
              disabled={isDisabledToEdit || isApprovedDeal || values.extendType === 'Reverse'}
              variant="outlined"
              fullWidth
              name="advanceAmount"
              onChange={e => {
                handleChange(e)
                if (!touched.advanceAmount) setFieldTouched('advanceAmount')
              }}
              value={values.advanceAmount ?? ''}
              error={Boolean(touched.advanceAmount && errors.advanceAmount)}
              helperText={touched.advanceAmount && errors.advanceAmount}
              label={<RequiredOption label="Advance Amount" />}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePickerWithTooltip
              disabled={isDisabledToEdit || isApprovedDeal}
              disablePast
              name="dealStartDate"
              value={values.startDate ? values.startDate : null}
              onChange={handleStartDateChange}
              error={Boolean(touched.startDate && errors.startDate) || !isValidDate(values.startDate)}
              helperText={(touched.startDate && errors.startDate) || (!isValidDate(values.startDate) && 'MM/DD/YYYY')}
              inputVariant="outlined"
              variant="dialog"
              type="keyboard"
              fullWidth
              label={<RequiredOption label="Deal start date" />}
              tooltipText="The date when the deal officially starts, the deal terms become active and the first disbursement is made."
            />
          </Grid>
          <Grid item xs={6}>
            <DatePickerWithTooltip
              disabled={isDisabledToEdit || isApprovedDeal}
              disablePast
              name="firstPaymentDate"
              value={values.firstPaymentDate ? values.firstPaymentDate : null}
              shouldDisableDate={(date) => {
                if (!values.startDate || !date) return false;
                return isBeforeDate(date, values.startDate); 
              }}
              onChange={handleFirstPaymentDateChange}
              error={Boolean(touched.firstPaymentDate && errors.firstPaymentDate) || !isValidDate(values.firstPaymentDate)}
              helperText={(touched.firstPaymentDate && errors.firstPaymentDate) || (!isValidDate(values.firstPaymentDate) && 'MM/DD/YYYY')}
              inputVariant="outlined"
              variant="dialog"
              type="keyboard"
              fullWidth
              label={<RequiredOption label="First payment date" />}
              tooltipText="The date when the customer’s first scheduled payment is due according to the payment schedule."
                />
          </Grid>
          {values.paymentFrequency === 'Customised' && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenCustomScheduleModal}
                fullWidth
              >
                Configure Customised Schedule
              </Button>
            </Grid>
          )}
          <Grid item xs={3} className={s.checkboxGrid}>
            <FormControlLabel
              disabled={isDisabledToEdit}
              classes={{
                label: s.checkboxLabel,
              }}
              name="uccUploaded"
              onChange={handleChange}
              label="UCC uploaded"
              control={<Checkbox disabled={isDisabledToEdit} checked={values.uccUploaded} color="primary" name="uccUploaded" />}
            />
          </Grid>
          <Grid item xs={3} className={s.checkboxGrid}>
            <FormControlLabel
              disabled={isDisabledToEdit || isApprovedDeal}
              classes={{
                label: s.checkboxLabel,
              }}
              name="isFlexDeal"
              onChange={(e, checked) => {
                handleChange(e)
                if(!checked){
                  clearMerchantPositions()
                } else {
                  clearCommissionDueDates()
                }
              }}
              label="Flex/Reverse Deal"
              control={<Checkbox checked={values.isFlexDeal} color="primary" name="isFlexDeal" />}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectComponent
              disabled={!values.isFlexDeal || isDisabledToEdit || isApprovedDeal}
              name="extendType"
              label={<RequiredOption label="Flex Type" />}
              onChange={e => {
                handleChange(e)
                if(e.target.value === 'Reverse'){
                  clearExternalDeals()
                } else {
                  clearMerchantPositions()
                }
              }}
              labelProps={{ className: s.selectLabel }}
              fullWidth
              error={Boolean(touched.extendType && errors.extendType)}
              helperText={touched.extendType && errors.extendType}
              value={values.extendType ?? ''}
            >
              {extendTypes.map(item => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </SelectComponent>
          </Grid>
          {values.extendType === 'Reverse' && (
            <>
              <Grid item xs={6}>
                <SelectComponent
                  disabled={isDisabledToEdit || isApprovedDeal}
                  name="disbursementFrequency"
                  label={<RequiredOption label="Disbursement Frequency" />}
                  onChange={handleChange}
                  labelProps={{ className: s.selectLabel }}
                  fullWidth
                  error={Boolean(touched.disbursementFrequency && errors.disbursementFrequency)}
                  helperText={touched.disbursementFrequency && errors.disbursementFrequency}
                  value={values.disbursementFrequency ?? ''}
                >
                  {(['Weekly', 'Daily'] as IPaymentFrequency[]).map(item => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </SelectComponent>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  disabled
                  variant="outlined"
                  fullWidth
                  name="reverseTerm"
                  onChange={handleChange}
                  value={values.reverseTerm ?? ''}
                  error={Boolean(touched.reverseTerm && errors.reverseTerm)}
                  helperText={touched.reverseTerm && errors.reverseTerm}
                  label={<RequiredOption label="Reverse Term" />}
                />
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            <DecimalTextField
              variant="outlined"
              fullWidth
              disabled={!values.isFlexDeal || isApprovedDeal || values.extendType === 'Reverse'}
              name="flexAmount"
              onChange={handleChange}
              value={values.flexAmount ?? ''}
              error={Boolean(touched.flexAmount && errors.flexAmount)}
              helperText={touched.flexAmount && errors.flexAmount}
              label={<RequiredOption label="Flex/Reverse Amount" />}
              InputProps={{
                endAdornment: isLoadingReverse ? (
                  <InputAdornment position="end">
                    <CircularProgress color="primary" size={20} />
                  </InputAdornment>
                ) : (
                  values.extendType && (
                    <InputAdornment position="end">
                      <IconButton disabled={!values.flexAmount} onClick={flexPopover.open}>
                        <SplitIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                ),
              }}
            />
            <Popover
              open={flexPopover.isOpen}
              anchorEl={flexPopover.anchorEl}
              onClose={flexPopover.close}
              PaperProps={{ classes: { root: s.popoverFlex } }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <div className={s.popoverContainer}>
                <FlexDisbursementForm
                  initialValues={flexDisbursementFormValues}
                  initialDealStartDate={values.startDate}
                  persistedDealStartDate={values.initialStartDate}
                  persistedSchedule={values.initialFlexSchedule}
                  firstDisbursement={{
                    id: -1,
                    date: values.startDate ?? data?.data.startDate,
                    amount: values.flexAmount,
                  }}
                  onResize={flexPopover.reposition}
                  onSubmit={handleSubmitFlexDisbursement}
                  bankFee={Number(values.syndicationFee || 0)}
                  syndicationFeeDistribution={values.syndicationFeeDistribution}
                  uccUploaded={values.uccUploaded}
                  sendAsWire={values.sendAsWire}
                  extendType={values.extendType}
                  disableDates={isApprovedDeal}
                />
              </div>
            </Popover>
            {Boolean(submitCount > 0 && (errors.startDate || flexScheduleError)) && (
              <FormHelperText error>{errors.startDate || flexScheduleError}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={4}>
            <TextField
              disabled={isDisabledToEdit || values.paymentFrequency === 'Customised'}
              variant="outlined"
              fullWidth
              name="term"
              onChange={handleChange}
              value={values.term}
              error={Boolean(touched.term && errors.term)}
              helperText={touched.term && errors.term}
              label={<RequiredOption label="Term" />}
            />
          </Grid>
          <Grid item xs={4}>
            <DecimalTextField
              decimalScale={DECIMAL_PLACES_PERCENTAGE}
              disabled={isDisabledFactorRate}
              variant="outlined"
              fullWidth
              name="factor"
              onChange={handleChange}
              value={values.factor}
              error={Boolean(touched.factor && errors.factor)}
              helperText={touched.factor && errors.factor}
              label={<RequiredOption label="Factor rate" />}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton disabled={!values.factor} onClick={factorPopover.open}>
                      <SplitIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Popover
              open={factorPopover.isOpen}
              anchorEl={factorPopover.anchorEl}
              onClose={factorPopover.close}
              PaperProps={{ classes: { root: s.root } }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <div className={s.popoverContainer}>
                <EarlyDiscountForm
                  classes={{ root: s.popoverTextField }}
                  initialValues={mapEarlyDiscountOptionsToIEarlyDiscount(values.earlyDiscountOptions ?? [])}
                  advanceAmount={values.advanceAmount}
                  disableStaticOptions={isApprovedDeal}
                  onSubmit={earlyDiscount => {
                    factorPopover.close()
                    setFieldValue('earlyDiscountOptions', mapIEarlyDiscountToOptions(earlyDiscount))
                  }}
                />
              </div>
            </Popover>
          </Grid>
          <Grid item xs={6}>
            <DecimalTextField
              variant="outlined"
              fullWidth
              disabled
              InputLabelProps={{ shrink: values.frequentPayment !== undefined }}
              name="frequentPayment"
              onChange={handleChange}
              value={values.frequentPayment}
              error={Boolean(touched.frequentPayment && errors.frequentPayment)}
              helperText={touched.frequentPayment && errors.frequentPayment}
              label="Daily Payment"
              InputProps={
                values.paymentFrequency === 'Customised'
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            placement="top"
                            arrow
                            title="Value represents the amount of the first payment in the custom schedule."
                          >
                            <Box display="flex" alignItems="center">
                              <InfoIcon width={16} height={16} />
                            </Box>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={6}>
            <DecimalTextField
              variant="outlined"
              fullWidth
              disabled
              name="paybackAmount"
              InputLabelProps={{ shrink: values.paybackAmount !== undefined }}
              onChange={handleChange}
              value={values.paybackAmount}
              error={Boolean(touched.paybackAmount && errors.paybackAmount)}
              helperText={
                (touched.paybackAmount && errors.paybackAmount) || Boolean(!values.earlyPayOffAmount)
                  ? touched.paybackAmount && errors.paybackAmount
                  : `Total Payback Amount: ${Number(values?.paybackAmount) - Number(values?.earlyPayOffAmount)}`
              }
              label="Payback Amount"
            />
          </Grid>
          <Grid item xs={6} className={s.checkboxGrid}>
            <FormControlLabel
              disabled={isApprovedDeal && !values.isRoundingEnabled}
              classes={{
                label: s.checkboxLabel,
              }}
              name="isRoundingEnabled"
              onChange={handleChange}
              label="Round Daily/Weekly Payments"
              control={<Checkbox checked={values.isRoundingEnabled} color="primary" name="isRoundingEnabled" />}
            />
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <ISORepSelector
              type="ISO"
              value={{
                ids: values.isoIds,
                fee: values.isoFee,
                splitCommission: values.isoSplitCommission,
                commissionDueDate: values.isoCommissionDueDate
              }}
              onChange={value => {
                if (value) {
                  setFieldValue('isoIds', value.ids)
                  setFieldValue('isoFee', value.fee)
                  setFieldValue('isoSplitCommission', value.splitCommission)
                  setFieldValue('isoCommissionDueDate', value.commissionDueDate)
                  setSelectedIsos(
                    value.ids?.map((isoId: number) => {
                      const iso = isoData?.data.find((i: IIsoListItemLight) => i.id === isoId)
                      return { id: isoId, name: iso?.businessName || '' }
                    }) || []
                  )
                } else {
                  setFieldValue('isoIds', undefined)
                  setFieldValue('isoFee', '')
                  setFieldValue('isoSplitCommission', [])
                  setFieldValue('isoCommissionDueDate', undefined)
                  setSelectedIsos([])
                }
              }}
              disabled={isDisabledIsoSelection}
              disableIsoCommissionDueDateField={isIsoCommissionDueDateDisabled}
              advanceAmount={values.advanceAmount}
              dealStartDate={values.startDate}
              hideCommissionDueDateField={values.isFlexDeal}
            />
            {Boolean(submitCount > 0 && errors.isoCommissionDueDate) && (
              <FormHelperText error>{errors.isoCommissionDueDate}</FormHelperText>
            )}
            {isIsoClawbackSent && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary" style={{ color: '#9aa0a6' }}>
                  A clawback has been applied
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <DecimalTextField
              variant="outlined"
              fullWidth
              disabled={isDisabledEarlyPayoff}
              name="earlyPayOffAmount"
              onChange={handleChange}
              value={values.earlyPayOffAmount}
              error={Boolean(touched.earlyPayOffAmount && errors.earlyPayOffAmount)}
              helperText={touched.earlyPayOffAmount && errors.earlyPayOffAmount}
              label="Early PayOff Amount"
            />
          </Grid>
          <Grid item container justify="flex-start" xs={6}>
            <Typography className={s.comission}>Total Commission:</Typography>
            <Typography className={s.comission}>{checkForNaN(totalCommission)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>
              {calcProportionInPercents(values?.advanceAmount, totalCommission)}
              {`% of advance amount`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {!state.roles.isISO && (
              <ISORepSelector
                type="Rep"
                value={{
                  ids: values.representativeIds,
                  fee: values.commissionToRep,
                  splitCommission: values.repSplitCommission,
                  commissionDueDate: values.repCommissionDueDate
                }}
                onChange={value => {
                  if (value) {
                    setFieldValue('representativeIds', value.ids)
                    setFieldValue('commissionToRep', value.fee)
                    setFieldValue('repSplitCommission', value.splitCommission)
                    setFieldValue('repCommissionDueDate', value.commissionDueDate)
                    if (representativeState) {
                      setSelectedReps([
                        representativeState,
                        ...(value.ids
                          ?.filter((repId: number) => repId !== 1)
                          .map((repId: number) => {
                            const rep = repData?.data.find((r: IRepListItemLight) => r.id === repId)
                            return { id: repId, name: rep?.name || '' }
                          }) || []),
                      ])
                    }
                  } else {
                    setFieldValue('representativeIds', undefined)
                    setFieldValue('commissionToRep', 0)
                    setFieldValue('repSplitCommission', [])
                    setSelectedReps([{ id: 1, value: 100 }])
                    setFieldValue('repCommissionDueDate', undefined)
                  }
                }}
                disabled={isDisabledRepSelection}
                disableRepCommissionDueDateField={isRepCommissionDueDateDisabled}
                advanceAmount={values.advanceAmount}
                dealStartDate={values.startDate}
                hideCommissionDueDateField={values.isFlexDeal}
              />
            )}
            {Boolean(submitCount > 0 && errors.repCommissionDueDate) && (
              <FormHelperText error>{errors.repCommissionDueDate}</FormHelperText>
            )}
            {isRepClawbackSent && (
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary" style={{ color: '#9aa0a6' }}>
                  A clawback has been applied
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={6}>
            {!state.roles.isISO && (
              <VendorAutoComplete
                multiple
                vendorId={values.vendorIds}
                disabled={data?.data.status !== DealsStatus.approved || !hasPermission(ADD_VENDOR_TO_DEAL)}
                label="Vendors"
                error={Boolean(touched.vendorIds && errors.vendorIds)}
                helperText={touched.vendorIds && errors.vendorIds}
                onChange={(e, value) => {
                  if (value)
                    handleChange({
                      target: {
                        name: 'vendorIds',
                        value: value.map((v: IVendorListLightItem) => v.id),
                      },
                    })
                  else {
                    handleChange({ target: { name: 'vendorIds', value: undefined } })
                  }
                }}
              />
            )}
          </Grid>
          {values.extendType !== 'Reverse' && (
            <Grid item xs={6}>
              <DecimalTextField
                disabled={isDisabledToEdit}
                variant="outlined"
                fullWidth
                name="psfFee"
                onChange={handleChange}
                value={values.psfFee}
                error={Boolean(touched.psfFee && errors.psfFee)}
                helperText={touched.psfFee && errors.psfFee}
                label="PSF Fee"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={!values.psfFee || !selectedReps.length || isDisabledSplitCommission}
                        aria-label="Split PSF Fee to Rep's"
                        onClick={psfPopover.open}
                      >
                        <SplitIcon height={60} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Popover
                open={psfPopover.isOpen}
                anchorEl={psfPopover.anchorEl}
                onClose={psfPopover.close}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                PaperProps={{ classes: { root: s.root } }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <div className={s.popoverContainer}>
                  <SplitCommissionForm
                    key="psfFeeSplitCommission"
                    handleClosePopover={psfPopover.close}
                    propsSetFieldValue={setFieldValue}
                    propsFormValues={values}
                    initialValues={getInitialValuesForCommissionLocal('psfFeeSplitCommission')}
                    selectedReps={selectedReps}
                    disabled={isDisabledToEdit}
                    classes={{ root: s.popoverTextField }}
                    name="psfFeeSplitCommission"
                    totalFeeAmount={Number(values.psfFee || 0)}
                  />
                </div>
              </Popover>
            </Grid>
          )}
          <Grid item xs={6}>
            <DecimalTextField
              disabled
              variant="outlined"
              fullWidth
              name="payOffAmount"
              onChange={handleChange}
              value={values.payOffAmount}
              error={Boolean(touched.payOffAmount && errors.payOffAmount)}
              helperText={touched.payOffAmount && errors.payOffAmount}
              label="Ref/PayOff Amount"
            />
          </Grid>

          <Grid item xs={values.extendType ? 6 : 12}>
            <DecimalTextField
              disabled={isDisabledToEdit || (values.extendType === 'Reverse' && isApprovedDeal)}
              variant="outlined"
              fullWidth
              name="syndicationFee"
              onChange={handleChange}
              value={values.syndicationFee ?? ''}
              error={Boolean(touched.syndicationFee && errors.syndicationFee)}
              helperText={touched.syndicationFee && errors.syndicationFee}
              label="Bank Fee"
            />
          </Grid>

          {(values.extendType === 'Flex' || values.extendType === 'Reverse') && (
            <Grid item xs={values.extendType === 'Reverse' ? 12 : 6}>
              <BankFeeDistributionSelector
                disabled={Boolean(values.isSubmitted) || (values.extendType === 'Reverse' && isApprovedDeal)}
                name="syndicationFeeDistribution"
                label="Bank Fee Distribution"
                labelProps={{ className: s.selectLabel }}
                fullWidth
                extendType={values.extendType}
                value={values.syndicationFeeDistribution}
                error={Boolean(touched.syndicationFeeDistribution && errors.syndicationFeeDistribution)}
                helperText={touched.syndicationFeeDistribution && errors.syndicationFeeDistribution}
                onChange={(v?: IFeeDistributionOption) => setFieldValue('syndicationFeeDistribution', v)}
              />
            </Grid>
          )}

          <Grid item xs>
            <DecimalTextField
              decimalScale={DECIMAL_PLACES_PERCENTAGE}
              variant="outlined"
              fullWidth
              name="specifiedPercentage"
              onChange={handleChange}
              value={values.specifiedPercentage ?? ''}
              error={Boolean(touched.specifiedPercentage && errors.specifiedPercentage)}
              helperText={touched.specifiedPercentage && errors.specifiedPercentage}
              label={<RequiredOption label="Specified Percentage" />}
            />
          </Grid>
          {['NY', 'CA'].includes(values.clientState ?? '') && (
            <Grid item xs={4}>
              <DecimalTextField
                decimalScale={DECIMAL_PLACES_PERCENTAGE}
                variant="outlined"
                fullWidth
                disabled
                name="apr"
                onChange={handleChange}
                value={values.apr ?? ''}
                error={Boolean(touched.apr && errors.apr)}
                helperText={touched.apr && errors.apr}
                label={<RequiredOption label="APR" />}
                InputLabelProps={{ shrink: values.apr !== undefined }}
              />
            </Grid>
          )}
          <Grid item xs>
            <DecimalTextField
              variant="outlined"
              fullWidth
              name="monthlyRevenue"
              onChange={handleChange}
              value={values.monthlyRevenue ?? ''}
              error={Boolean(touched.monthlyRevenue && errors.monthlyRevenue)}
              helperText={touched.monthlyRevenue && errors.monthlyRevenue}
              label={<RequiredOption label="Monthly Revenue" />}
            />
          </Grid>
        </Grid>
      </Paper>
      <Paper className={s.paper}>
        <Box>
          <Typography className={s.heading}>Business Banking Info</Typography>
          <Grid container spacing={8}>
            <Grid item xs={12}>
              <SelectComponent
                disabled={isDisabledToEdit}
                name="bankAccountId"
                label={<RequiredOption label="Bank Account" />}
                onChange={handleChange}
                labelProps={{ className: s.selectLabel }}
                fullWidth
                error={Boolean(touched.bankAccountId && errors.bankAccountId)}
                helperText={touched.bankAccountId && errors.bankAccountId}
                value={values.bankAccountId}
              >
                {dataBankAccounts && dataBankAccounts.data.length > 0 ? (
                  dataBankAccounts.data.map(ba => (
                    <MenuItem key={ba.bankAccount.id} value={ba.bankAccount.id}>
                      {ba.bankAccount.bankName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    {dataBankAccounts ? 'No bank accounts' : 'Choose Client'}
                  </MenuItem>
                )}
              </SelectComponent>
            </Grid>
            <Grid item xs={12} className={s.checkboxGrid}>
              <FormControlLabel
                disabled={isDisabledToEdit}
                classes={{
                  label: s.checkboxLabel,
                }}
                name="sendAsWire"
                onChange={handleChange}
                label="Send As Wire"
                control={<Checkbox checked={values.sendAsWire} color="primary" name="sendAsWire" />}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                disabled={isDisabledToEdit}
                variant="outlined"
                fullWidth
                name="comments"
                onChange={handleChange}
                value={values.comments}
                error={Boolean(touched.comments && errors.comments)}
                helperText={touched.comments && errors.comments}
                label="Comments"
                multiline
                rows={5}
              />
            </Grid>
          </Grid>
        </Box>

        <Box marginTop={10}>
          <Typography className={s.heading}>Notification Preferences</Typography>

          {/* Map over the default list of ALL possible settings */}
          {notificationSettings.map((defaultSetting, index, array) => {
            
            // Check if the user/backend actually has this setting active in Formik
            const activeSetting = values.notificationSubscriptionSettings.find(
              (setting) => setting.notificationType === defaultSetting.notificationType
            );

            return (
              <React.Fragment key={defaultSetting.notificationType}>
                <NotificationSubscriptionForm 
                  initialValues={activeSetting || defaultSetting} 
                  isActive={Boolean(activeSetting)}
                  classes={s} 
                  onChange={handleNotificationPreferencesChanged} 
                />
                
                {index < array.length - 1 && (
                  <Box my={4}>
                    <Divider />
                  </Box>
                )}
              </React.Fragment>
            )
          })}
        </Box>

        <Box marginTop="auto">
          {!isApprovedDeal && (
            <Button variant="contained" onClick={goBack} startIcon={<ArrowLeftIcon className={s.icon} />} className={s.button}>
              Back
            </Button>
          )}
          <ButtonWithPreloader
            loading={isCreateLoading || isUpdateLoading}
            disabled={isCreateLoading || isUpdateLoading || isDisabledToEdit}
            type="submit"
            color="primary"
            variant="contained"
          >
            {isApprovedDeal ? 'Save' : 'Complete'}
          </ButtonWithPreloader>
        </Box>
      </Paper>
      {values.extendType !== 'Reverse' && (
        <Paper className={s.paper}>
          <Box display="flex" alignItems="baseline" justifyContent="space-between">
            <Typography className={s.heading}>External Deals</Typography>
            {!isDisabledToEdit && !isApprovedDeal && (
              <Button onClick={externalDealsPopover.open} color="secondary" variant="text" startIcon={<PlusIcon />}>
                Add external deal
              </Button>
            )}
          </Box>
          <Box className={s.tableContainer}>
            <ExternalDealsTable
              externalDeals={values.externalDeals}
              removeExternalDeal={i => setEditingExternalDeal({ deleteId: i })}
              editExternalDeal={setEditingExternalDeal}
              disabled={isDisabledToEdit || isApprovedDeal}
            />
          </Box>
          <Popover
            open={externalDealsPopover.isOpen}
            anchorEl={externalDealsPopover.anchorEl}
            onClose={() => {
              setEditingExternalDeal(undefined)
              externalDealsPopover.close()
            }}
            PaperProps={{ classes: { root: s.popoverPositions } }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
          >
            <div className={s.popoverContainer}>
              <ExternalDealsForm
                initialValues={editingExternalDeal?.externalDeal}
                onCancel={externalDealsPopover.close}
                onSubmit={ed => {
                  if (editingExternalDeal?.externalDeal) {
                    setTimeout(() => handleEditExternalDeal(ed), 0)
                    setEditingExternalDeal(undefined)
                  } else handleAddExternalDeal(ed)
                  externalDealsPopover.close()
                }}
              />
            </div>
          </Popover>
          <ModalComponent open={editingExternalDeal?.deleteId !== undefined}>
            <ConfirmationContent
              text="Are you sure you want to delete external deal?"
              handleCancel={() => setEditingExternalDeal(undefined)}
              handleConfirm={() => {
                editingExternalDeal?.deleteId && handleRemoveExternalDeal(editingExternalDeal.deleteId)
                setEditingExternalDeal(undefined)
              }}
            />
          </ModalComponent>
        </Paper>
      )}
      {values.extendType === 'Reverse' && selectedClient && (
        <Paper className={s.paper}>
          <Box display="flex" alignItems="baseline" justifyContent="space-between">
            <Typography className={s.heading}>Current positions</Typography>
            {!isDisabledToEdit && !isApprovedDeal && (
              <Button onClick={positionsPopover.open} color="secondary" variant="text" startIcon={<PlusIcon />}>
                Add external position
              </Button>
            )}
          </Box>
          <Box className={s.tableContainer}>
            <MerchantPositionsTable
              clientId={selectedClient.id}
              positions={values.merchantPositions}
              addPosition={handleAddMerchantPosition}
              removePosition={i => setEditingPosition({ deleteId: i })}
              editPosition={setEditingPosition}
              disabled={isDisabledToEdit || isApprovedDeal}
            />
          </Box>
          <Popover
            open={positionsPopover.isOpen}
            anchorEl={positionsPopover.anchorEl}
            onClose={() => {
              setEditingPosition(undefined)
              positionsPopover.close()
            }}
            PaperProps={{ classes: { root: s.popoverPositions } }}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
          >
            <div className={s.popoverContainer}>
              <MerchantPositionsForm
                clientId={selectedClient.id}
                initialValues={editingPosition?.position}
                onCancel={positionsPopover.close}
                onSubmit={p => {
                  if (editingPosition?.position) {
                    setTimeout(() => handleEditMerchantPosition(p), 0)
                    setEditingPosition(undefined)
                  } else handleAddMerchantPosition(p)
                  positionsPopover.close()
                }}
              />
            </div>
          </Popover>
          <ModalComponent open={editingPosition?.deleteId !== undefined}>
            <ConfirmationContent
              text="Are you sure you want to delete position?"
              handleCancel={() => setEditingPosition(undefined)}
              handleConfirm={() => {
                editingPosition?.deleteId && handleRemoveMerchantPosition(editingPosition.deleteId)
                setEditingPosition(undefined)
              }}
            />
          </ModalComponent>
        </Paper>
      )}
      <CustomizedScheduleModal
        open={customScheduleModalOpen}
        onClose={handleCloseCustomScheduleModal}
        onSave={handleSaveCustomSchedule}
        onError={handleCustomScheduleError}
        initialSchedule={values.customPaymentSchedule}
        dealStartDate={values.startDate}
        initialFirstPaymentDate={values.firstPaymentDate}
        totalPaybackAmount={Number(values.paybackAmount) || 0}
        isFirstPaymentDateEditable={!isApprovedDeal}
      />
    </Box>
  )
}

export default DealInformationForm
